// Tiny in-memory cache + in-flight de-duplication for GET requests.
//
// Why: several public, read-only endpoints are fetched by more than one
// component on the same screen (e.g. `/api/site-elements/public` is loaded
// once per <CustomElements> slot — 3× on every content page). React 18
// StrictMode also double-invokes effects in development, doubling every
// naive fetch. Both collapse to a single network request when the call goes
// through `cachedGet`.
//
// Scope: use this ONLY for public, non-personalised GET payloads that are
// safe to serve slightly stale (site settings, banners, services, coverage,
// custom elements). Admin/master screens keep using raw `axios` so their
// reads are always live; when they mutate one of these resources they should
// call `invalidateApiCache(url)` so visitors pick the change up immediately.

import axios from 'axios';

const DEFAULT_TTL_MS = 30_000;

// url -> { ts: number, data: any } | { promise: Promise<any> }
const store = new Map();

/**
 * GET `url`, sharing the result across callers.
 *  - concurrent callers for the same url await the same request (dedupe)
 *  - a resolved response is reused for `ttl` ms (default 30s)
 *
 * @param {string} url
 * @param {{ ttl?: number, config?: import('axios').AxiosRequestConfig }} [opts]
 * @returns {Promise<any>} the response body (res.data)
 */
export function cachedGet(url, opts = {}) {
  const { ttl = DEFAULT_TTL_MS, config } = opts;
  const entry = store.get(url);

  if (entry) {
    if (entry.promise) return entry.promise; // request already in flight
    if (Date.now() - entry.ts < ttl) return Promise.resolve(entry.data); // fresh
  }

  const promise = axios
    .get(url, config)
    .then((res) => {
      store.set(url, { ts: Date.now(), data: res.data });
      return res.data;
    })
    .catch((err) => {
      store.delete(url); // don't cache failures
      throw err;
    });

  store.set(url, { promise });
  return promise;
}

/** Drop a cached entry (or everything) — call after mutating the resource. */
export function invalidateApiCache(url) {
  if (url) store.delete(url);
  else store.clear();
}
